/* eslint-disable react/no-array-index-key */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import classNames from 'classnames';
import { injectIntl } from 'react-intl';
import { TimelineMax, TweenMax } from 'gsap';

import BoxButton from '../partials/BoxButton';
import Background from '../partials/Background';

import styles from '../../../styles/blocks/slideshow.scss';

const propTypes = {
    index: PropTypes.number,
    width: PropTypes.number,
    height: PropTypes.number,
    slides: PropTypes.arrayOf(PropTypes.shape({
        page: PropTypes.shape({
            title: PropTypes.string,
            image: PropTypes.string,
        }),
        title: PropTypes.objectOf(PropTypes.string),
        description: PropTypes.objectOf(PropTypes.string),
        background: PropTypes.shape({
            type: PropTypes.oneOf(['video', 'image', 'color']),
        }),
    })),
    looping: PropTypes.bool,
    arrowNav: PropTypes.bool,
    lowerNav: PropTypes.bool,
    intl: PropTypes.shape({
        locale: PropTypes.string,
    }).isRequired,
};

const defaultProps = {
    index: 0,
    width: null,
    height: null,
    slides: [],
    looping: false,
    arrowNav: false,
    lowerNav: false,
};

class Slideshow extends Component {
    constructor(props) {
        super(props);

        this.renderSlide = this.renderSlide.bind(this);
        this.onClickDot = this.onClickDot.bind(this);
        this.onComplete = this.onComplete.bind(this);

        this.onClickNext = this.onClickNext.bind(this);
        this.onClickPrev = this.onClickPrev.bind(this);

        this.slideTween = null;
        this.labelTween = null;
        this.refSlides = [];
        this.refLowerLabel = null;

        this.loopTween = null;

        this.state = {
            index: props.index,
            paused: false,
            lowerLabel: '',
        };
    }

    componentDidMount() {
        const { looping } = this.props;
        const { index } = this.state;

        this.refSlides.forEach((refItem, itemIndex) => {
            TweenMax.set(refItem, {
                xPercent: itemIndex === index ? '0' : '100',
            });
        });

        this.updateLabel();

        if (looping) {
            this.loopTween = new TimelineMax({
                loop: true,
                repeat: -1,
            });
            const dummy = { val: 0 };
            this.loopTween.to(dummy, 10, {
                val: 1,
                onComplete: () => {
                    dummy.val = 1;
                    this.onComplete();
                },
            });
        }
    }

    componentDidUpdate(prevProps, prevState) {
        const indexChanged = prevState.index !== this.state.index;
        if (indexChanged) {
            this.gotoSlide(this.state.index, prevState.index);
            this.updateLabel();
        }
    }

    componentWillUnmount() {
        if (this.slideTween !== null) {
            this.slideTween.kill();
            this.slideTween = null;
        }
        if (this.labelTween !== null) {
            this.labelTween.kill();
            this.labelTween = null;
        }
        if (this.loopTween !== null) {
            this.loopTween.kill();
            this.loopTween = null;
        }
    }

    onClickDot(e, slide, index) {
        this.setState({
            index,
            // paused: true,
        });
    }

    onClickNext() {
        const { slides } = this.props;
        const { index } = this.state;
        this.setState({
            index: index === slides.length - 1 ? 0 : index + 1,
        });
    }

    onClickPrev() {
        const { slides } = this.props;
        const { index } = this.state;
        this.setState({
            index: index === 0 ? slides.length - 1 : index - 1,
        });
    }

    onComplete() {
        const { slides } = this.props;
        const { index, paused } = this.state;
        if (!paused) {
            this.setState({
                index: index === slides.length - 1 ? 0 : index + 1,
            });
        }
    }

    nextSlide() {
        const { slides } = this.props;
        const { index } = this.state;
        this.gotoSlide(index === slides.length - 1 ? 0 : index + 1);
    }

    prevSlide() {
        const { slides } = this.props;
        const { index } = this.state;
        this.gotoSlide(index === 0 ? slides.length - 1 : index - 1);
    }

    gotoSlide(nextIndex, index) {
        const currentIndex = typeof index !== 'undefined' ? index : this.state.index;
        if (this.slideTween !== null) {
            this.slideTween.kill();
            this.slideTween = null;
        }
        if (this.labelTween !== null) {
            this.labelTween.kill();
            this.labelTween = null;
        }
        if (this.loopTween !== null) {
            this.loopTween.restart();
        }

        const currentSlide = this.refSlides[currentIndex];
        const nextSlide = this.refSlides[nextIndex];
        const direction = nextIndex > index ? 'left' : 'right';
        this.slideTween = new TimelineMax();
        this.slideTween.to(
            currentSlide,
            0.5,
            {
                xPercent: direction === 'left' ? '-100%' : '100%',
            },
            0,
        );
        this.slideTween.fromTo(
            nextSlide,
            0.5,
            {
                xPercent: direction === 'left' ? '100%' : '-100%',
            },
            {
                xPercent: 0,
            },
            0,
        );
    }

    updateLabel() {
        const { slides, intl } = this.props;
        const { index } = this.state;
        const { locale } = intl;
        const slide = slides[index];
        const description = get(slide, `page.description.${locale}`, null);

        if (this.refLowerLabel !== null) {
            this.labelTween = new TimelineMax();
            this.labelTween.to(this.refLowerLabel, 0.3, {
                alpha: 0,
                onComplete: () => {
                    this.setState({
                        lowerLabel: description,
                    });
                },
            });
            this.labelTween.to(this.refLowerLabel, 0.3, {
                alpha: 1,
            });
        } else {
            this.setState({
                lowerLabel: description,
            });
        }
    }

    // eslint-disable-next-line class-methods-use-this
    renderSlideBackground(background, index) {
        const { width, height } = this.props;
        const current = this.state.index === index;
        return (
            <div className={styles.background}>
                <Background
                    {...background}
                    width={width}
                    height={height}
                    current={current}
                />
            </div>
        );
    }

    renderSlide(slide, index) {
        const { intl } = this.props;
        const { locale } = intl;

        const page = get(slide, 'page', null);
        const pageImage = get(page, 'image', null);
        const pageVideo = get(page, 'data.videos.0', null);
        const pageImageBackground =
            pageImage !== null
                ? {
                    type: 'image',
                    image: {
                        url: pageImage,
                    },
                }
                : null;
        const pageVideoBackground =
            pageVideo !== null
                ? {
                    type: 'video',
                    video: pageVideo,
                }
                : null;
        const hasPageBackground = get(page, 'background.type', null) !== null;
        const pageBackground =
            (hasPageBackground ? get(page, 'background', null) : null) ||
            pageVideoBackground ||
            pageImageBackground;
        const isProduction = page !== null && page.type === 'production';
        const directorTitle = isProduction ? get(page, 'data.filmInfo.director', null) : null;
        const productionTitle =
            isProduction && !isEmpty(directorTitle)
                ? intl.formatMessage(
                    {
                        id: 'content.a_film_by',
                        defaultMessage: 'A film by {name}',
                    },
                    {
                        name: directorTitle,
                    },
                )
                : null;
        const title = get(slide, `title.${locale}`, get(page, 'title', null));
        const description = get(
            slide,
            `description.${locale}`,
            productionTitle || get(page, 'summary', null),
        );
        const link = get(slide, 'url', get(page, 'url', '/'));
        const hasBackground = get(slide, 'background.type', null) !== null;
        const background = hasBackground
            ? get(slide, 'background', null) || pageBackground
            : pageBackground;
        return (
            <div
                key={`slide-${index}`}
                className={styles.slide}
                ref={(ref) => {
                    this.refSlides[index] = ref;
                }}
            >
                <div className={styles.inner}>
                    <div className={styles.cols}>
                        <div className={styles.middle}>
                            <div className={styles.label}>
                                {title !== null ? <h1 className={styles.title}>{title}</h1> : null}
                                {description !== null ? (
                                    <div className={styles.description}>{description}</div>
                                ) : null}
                                {link !== null ? (
                                    <div className={styles.link}>
                                        <BoxButton link={link} />
                                    </div>
                                ) : null}
                            </div>
                        </div>
                    </div>
                </div>
                <div className={styles.gradient} />
                {background !== null ? this.renderSlideBackground(background, index) : null}
            </div>
        );
    }

    renderSlides() {
        const { slides } = this.props;
        return <div className={styles.slides}>{slides.map(this.renderSlide)}</div>;
    }

    renderLowerLabel() {
        const { lowerLabel } = this.state;
        return lowerLabel !== null ? (
            <div
                className={styles.lowerLabel}
                ref={(ref) => {
                    this.refLowerLabel = ref;
                }}
            >
                {lowerLabel !== null ? (
                    <div className={styles.description}>{lowerLabel}</div>
                ) : null}
            </div>
        ) : null;
    }

    renderArrowNav() {
        return (
            <div className={styles.arrowNav}>
                <div className={styles.arrows}>
                    <button
                        className={classNames({
                            [styles.arrow]: true,
                            [styles.arrowLeft]: true,
                        })}
                        onClick={this.onClickPrev}
                    />
                    <button
                        className={classNames({
                            [styles.arrow]: true,
                            [styles.arrowRight]: true,
                        })}
                        onClick={this.onClickNext}
                    />
                </div>
            </div>
        );
    }

    renderNavigation() {
        const { slides } = this.props;
        const { index } = this.state;
        return (
            <div className={styles.navigation}>
                <ul className={styles.items}>
                    {slides.map((slide, slideIndex) => (
                        <li
                            key={`dot-${slideIndex}`}
                            className={classNames({
                                [styles.item]: true,
                                [styles.current]: index === slideIndex,
                            })}
                        >
                            <button
                                type="button"
                                className={styles.dot}
                                onClick={e => this.onClickDot(e, slide, slideIndex)}
                            >
                                <span className={styles.icon} />
                            </button>
                        </li>
                    ))}
                </ul>
            </div>
        );
    }

    render() {
        const { slides, lowerNav, arrowNav } = this.props;
        const className = classNames({
            [styles.container]: true,
            [styles.lowerNav]: lowerNav,
        });
        return (
            <div className={className}>
                {this.renderSlides()}
                {slides.length > 1 ? this.renderNavigation() : null}
                {lowerNav ? this.renderLowerLabel() : null}
                {arrowNav ? this.renderArrowNav() : null}
            </div>
        );
    }
}

Slideshow.propTypes = propTypes;
Slideshow.defaultProps = defaultProps;

const WithIntlContainer = injectIntl(Slideshow);
export default WithIntlContainer;



// WEBPACK FOOTER //
// ./js/components/blocks/Slideshow.jsx